import {
  animate,
  AnimationBuilder,
  AnimationFactory,
  AnimationPlayer,
  style,
} from '@angular/animations';
import {
  Directive,
  effect,
  ElementRef,
  input,
  Input,
  Renderer2,
} from '@angular/core';
@Directive({
  selector: '[zSideDrawer]',
  standalone: true,
})
export class SideDrawerDirective {
  maxWidth = input<string>();

  @Input() set isOpen(value: boolean) {
    this._isOpen = value;
    if (this._direction) this.toggleDrawer();
  }

  @Input() set direction(value: 'left' | 'right') {
    this._direction = value;
    this.setupDrawer(value);
    this.createBackdrop();
    this.createAnimations(value);
    this.toggleDrawer();
  }

  private _isOpen = false;
  private _direction!: 'left' | 'right';
  private backdrop!: HTMLDivElement;
  private openAnimation!: AnimationFactory;
  private closeAnimation!: AnimationFactory;
  private player!: AnimationPlayer;

  constructor(
    readonly el: ElementRef,
    readonly renderer: Renderer2,
    readonly animationBuilder: AnimationBuilder,
  ) {
    effect(
      () => {
        if (this.maxWidth()) {
          if (this.el) {
            this.renderer.setStyle(
              this.el.nativeElement,
              'width',
              this.maxWidth(),
            );
          }
        }
      },
      { allowSignalWrites: true },
    );
  }

  private setupDrawer(dir: 'left' | 'right') {
    this.renderer.setStyle(this.el.nativeElement, 'position', 'absolute');
    this.renderer.setStyle(this.el.nativeElement, 'top', '0');
    this.renderer.setStyle(this.el.nativeElement, 'bottom', '0');
    this.renderer.setStyle(this.el.nativeElement, 'z-index', '999');
    this.renderer.setStyle(this.el.nativeElement, 'background', '#FFF');
    this.renderer.setStyle(this.el.nativeElement, dir, '0');
  }

  private createAnimations(dir: 'left' | 'right') {
    this.openAnimation = this.animationBuilder.build([
      style({
        transform: dir === 'left' ? 'translateX(-100%)' : 'translateX(100%)',
      }),
      animate('300ms ease-in', style({ transform: 'translateX(0)' })),
    ]);

    this.closeAnimation = this.animationBuilder.build([
      animate(
        '300ms ease-out',
        style({
          transform: dir === 'left' ? 'translateX(-100%)' : 'translateX(100%)',
        }),
      ),
    ]);
  }

  private toggleDrawer() {
    this.player?.destroy();

    this.player = this._isOpen
      ? this.openAnimation.create(this.el.nativeElement)
      : this.closeAnimation.create(this.el.nativeElement);
    this.player.play();

    if (this._isOpen) {
      this.showBackdrop();
    } else {
      this.hideBackdrop();
    }
  }

  private createBackdrop() {
    this.backdrop = this.renderer.createElement('div');
    this.renderer.addClass(this.backdrop, 'side-drawer-backdrop');
    this.renderer.setStyle(this.backdrop, 'position', 'absolute');
    this.renderer.setStyle(this.backdrop, 'inset', '0');
    this.renderer.setStyle(this.backdrop, 'z-index', '998');
    this.renderer.setStyle(
      this.backdrop,
      'background-color',
      'rgba(0, 0, 0, 0.3)',
    );
    this.renderer.setStyle(this.backdrop, 'display', 'none');
    this.renderer.appendChild(
      this.el.nativeElement.parentElement,
      this.backdrop,
    );
  }

  private showBackdrop() {
    this.renderer.setStyle(this.backdrop, 'display', 'block');
    setTimeout(() => {
      this.renderer.setStyle(this.backdrop, 'opacity', '0.9');
    });
  }

  private hideBackdrop() {
    this.renderer.setStyle(this.backdrop, 'opacity', '0');
    setTimeout(() => {
      this.renderer.setStyle(this.backdrop, 'display', 'none');
    }, 300); // Match the transition duration
  }
}
